import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Col, Form, Input, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../../common/constants";
import { DEFAULT_FILTER_COMMISSIONS_BATCHES_QUERY_PARAMS } from "../../../api";
import { CommissionsBatchStep, commissionsBatchStepTMap } from "../../../enums";
import { CommissionsBatchFilterPageRequest } from "../../../types";
import { CommissionsBatchStepTag } from "../../CommissionsBatchStepTag";

const colSpan = 6;

interface Props {
  searchParams: CommissionsBatchFilterPageRequest;
}

export const CommissionsBatchFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<CommissionsBatchFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({ keyword: searchParams.keyword, steps: searchParams.steps });
  }, [searchParams, form]);

  const handleFilterSubmit = (filter: CommissionsBatchFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...skipInitialFormValues(filter, DEFAULT_FILTER_COMMISSIONS_BATCHES_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="commissionsBatchSearchForm">
      <Row gutter={rowGutter} justify="center">
        <Col span={colSpan}>
          <Form.Item
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("commissions.batch.helpers.searchHint")} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item name="steps" label={t("commissions.batch.enums.batchStep._label")} rules={[validations.none]}>
            <Select
              {...selectTagsStandardProps(commissionsBatchStepTMap)}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              tagRender={(props) => (
                <CommissionsBatchStepTag
                  step={props.value}
                  closable={props.closable}
                  onClose={props.onClose}
                  label={props.label}
                />
              )}
              options={Object.values(CommissionsBatchStep).map((step) => ({
                value: step,
                label: <CommissionsBatchStepTag step={step} />,
              }))}
            />
          </Form.Item>
        </Col>

        <Col flex="110px" className="no-title-space">
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
              {t("common.filter")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
